import React, { useEffect, useState } from "react";
import { Check, Loader2, LogIn, MessageCircle } from "lucide-react";
import { useCommenter } from "../context/CommenterContext";
import { listImageEligiblePosts, postComment, startCommenterLogin, uploadCommentImage } from "../lib/commenter";

// Shared "Post to Match Preview Comments" modal used by both the Lineup
// Builder and the Classic Lineup Builder — takes a pre-generated lineup
// JPEG (or a generator fallback) and posts it as a comment image on a
// match preview post that has image uploads enabled.
export default function PostToCommentsModal({ onClose, generatedImage, generatePitchImage, defaultComment }) {
  const { user, loading: userLoading } = useCommenter();
  const [posts, setPosts] = useState(null);
  const [postId, setPostId] = useState("");
  const [comment, setComment] = useState("");
  const [previewImage, setPreviewImage] = useState(generatedImage || "");
  const [generating, setGenerating] = useState(!generatedImage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    listImageEligiblePosts()
      .then((rows) => {
        setPosts(rows);
        if (rows.length) setPostId(rows[0].id);
      })
      .catch(() => setPosts([]));
  }, []);

  useEffect(() => {
    if (previewImage) return;
    let alive = true;
    generatePitchImage()
      .then((img) => { if (alive) setPreviewImage(img || ""); })
      .catch(() => { if (alive) setError("Couldn't generate the lineup image. Please try again."); })
      .finally(() => { if (alive) setGenerating(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!postId || !previewImage || busy) return;
    setBusy(true);
    setError("");
    try {
      const url = await uploadCommentImage(previewImage);
      const body = comment.trim() || defaultComment || "Shared my Coventry City lineup 🔵⚪";
      await postComment(postId, body, null, url);
      setSuccess(true);
      setTimeout(onClose, 1600);
    } catch (err) {
      if (err.response?.status === 401) {
        startCommenterLogin();
        return;
      }
      setError(err.response?.data?.detail || err.message || "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="post-to-comments-modal">
      <div className="w-full max-w-md rounded-md bg-white p-6 shadow-xl animate-scale-up max-h-[90vh] overflow-y-auto scrollbar-thin">
        <div className="flex items-center justify-between border-b border-black/5 pb-2">
          <h3 className="font-display text-lg font-black text-[var(--brand-ink)] flex items-center gap-1.5">
            <MessageCircle size={18} className="text-brand-accent" />
            Post to Match Comments
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            data-testid="post-to-comments-close"
            className="text-[var(--brand-body)] hover:text-[var(--brand-ink)]"
          >
            ✕
          </button>
        </div>

        {success ? (
          <p className="mt-4 flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-3 text-sm font-semibold text-emerald-700" data-testid="post-to-comments-success">
            <Check size={16} /> Posted! Your lineup is now in that post's comments.
          </p>
        ) : (
          <>
            <div className="mt-4 flex justify-center rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-3">
              {generating ? (
                <div className="flex h-40 flex-col items-center justify-center gap-2 text-xs font-semibold text-[var(--brand-body)]">
                  <Loader2 size={20} className="animate-spin text-[#6CABDD]" /> Generating your lineup image…
                </div>
              ) : previewImage ? (
                <img src={previewImage} alt="Your lineup" className="max-h-52 rounded-sm" />
              ) : (
                <p className="py-8 text-xs text-[var(--brand-body)]">Couldn't generate an image.</p>
              )}
            </div>

            {userLoading ? null : !user ? (
              <button
                type="button"
                onClick={startCommenterLogin}
                data-testid="post-to-comments-signin"
                className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md border border-black/15 bg-white px-4 py-2 font-display text-sm font-bold text-[var(--brand-ink)] hover:bg-[#0A0F17] hover:text-white"
              >
                <LogIn size={14} /> Sign in with Google to post
              </button>
            ) : (
              <form onSubmit={submit} className="mt-4 space-y-3">
                <div>
                  <label className="label-kicker text-[var(--brand-body)]">Match preview post</label>
                  {posts === null ? (
                    <p className="mt-1 text-xs text-[var(--brand-body)]">Loading posts…</p>
                  ) : posts.length === 0 ? (
                    <p className="mt-1 text-xs text-[var(--brand-body)]">
                      No posts currently accept lineup images. Ask the admin to turn on
                      "Allow image uploads in comments" for a match preview post.
                    </p>
                  ) : (
                    <select
                      value={postId}
                      onChange={(e) => setPostId(e.target.value)}
                      data-testid="post-to-comments-select"
                      className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                    >
                      {posts.map((p) => (
                        <option key={p.id} value={p.id}>{p.title}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className="label-kicker text-[var(--brand-body)]">Comment (optional)</label>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    maxLength={2000}
                    rows={3}
                    placeholder="Say something about your XI…"
                    data-testid="post-to-comments-textarea"
                    className="mt-1 block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm outline-none focus:border-[#0A0F17]"
                  />
                </div>

                {error ? (
                  <p className="rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm" data-testid="post-to-comments-error">
                    {error}
                  </p>
                ) : null}

                <button
                  type="submit"
                  disabled={!postId || posts?.length === 0 || generating || !previewImage || busy}
                  data-testid="post-to-comments-submit"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : null}
                  {busy ? "Posting…" : "Post to Comments"}
                </button>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
}
